// Keeps the 0.00 mystery gift in step with the cart: in the cart from 2 paid items, out below that,
// never more than 1. Paid items = everything except the gift itself.
// The gift card in the cart already follows the paid count in Liquid, so the shopper sees it at once;
// this script only fixes the real cart line in the background, in one request that also returns the
// re-rendered cart sections, and hands those to the theme (no extra render round trip).
import { CartUpdateEvent, ThemeEvents } from '@theme/events';

const SOURCE = 'mystery-gift';
const UNLOCK_AT = 2;
const config = document.querySelector('script[data-mystery-gift-variant]');
const GIFT_VARIANT = Number(config?.dataset.mysteryGiftVariant || 0);
const root = window.Shopify?.routes?.root || '/';

let busy = false;
let again = false;
let lastFailedFor = null;   // cart state we already failed on, so a missing gift never loops

async function cartRequest(url, body) {
  const res = await fetch(url, {
    method: body ? 'POST' : 'GET',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw new Error(`${url} ${res.status}`);
  return res.json();
}

function sectionIds() {
  return [...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
}

function state(cart) {
  const lines = cart.items.filter((i) => i.variant_id === GIFT_VARIANT);
  const qty = lines.reduce((n, i) => n + i.quantity, 0);
  return { lines, qty, paid: cart.item_count - qty };
}

async function sync(knownCart) {
  if (!GIFT_VARIANT) return;
  if (busy) { again = true; return; }
  busy = true;
  try {
    const cart = knownCart?.items ? knownCart : await cartRequest(`${root}cart.js`);
    const { lines, qty, paid } = state(cart);
    if (`${paid}:${qty}` === lastFailedFor) return;

    const sections = sectionIds();
    const extra = sections.length ? { sections: sections.join(','), sections_url: window.location.pathname } : {};
    let updated = null;
    if (paid >= UNLOCK_AT && qty === 0) {
      // add.js returns the added line plus the sections; the new item count is simply one more
      const added = await cartRequest(`${root}cart/add.js`, { items: [{ id: GIFT_VARIANT, quantity: 1 }], ...extra });
      updated = { ...cart, item_count: cart.item_count + 1, sections: added.sections };
    } else if (paid >= UNLOCK_AT && qty > 1) {
      updated = await cartRequest(`${root}cart/change.js`, { id: lines[0].key, quantity: 1, ...extra });
    } else if (paid < UNLOCK_AT && qty > 0) {
      updated = await cartRequest(`${root}cart/change.js`, { id: lines[0].key, quantity: 0, ...extra });
    }

    if (updated) {
      document.dispatchEvent(new CartUpdateEvent(updated, SOURCE, {
        source: SOURCE,
        itemCount: updated.item_count,
        sections: updated.sections,
      }));
    }
  } catch (e) {
    console.warn('mystery gift:', e);
    try {
      const { qty, paid } = state(await cartRequest(`${root}cart.js`));
      lastFailedFor = `${paid}:${qty}`;
    } catch (_) { /* ignore */ }
  } finally {
    busy = false;
    if (again) { again = false; sync(); }
  }
}

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  if (event.detail?.sourceId === SOURCE) return;
  const resource = event.detail?.resource;
  // let the theme's own handlers run first, then sync (reuse the cart from the event when it has one)
  setTimeout(() => sync(resource), 0);
});

sync();
